import React, { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserPlus, ScanFace, Loader2, Trash2, CheckCircle2, XCircle, MapPin, WifiOff, CloudUpload } from "lucide-react";
import { loadFaceModels, computeDescriptor, distance } from "@/lib/faceApi";
import { enqueueAttendance, drainAttendance, isOnline, queueSize, subscribeOnline } from "@/lib/offlineQueue";

export default function Attendance() {
  const [workers, setWorkers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [modelReady, setModelReady] = useState(false);
  const [modelLoading, setModelLoading] = useState(true);
  const [mode, setMode] = useState("punch"); // punch | register | history
  const [history, setHistory] = useState([]);
  const [online, setOnline] = useState(isOnline());
  const [queued, setQueued] = useState(queueSize());
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadFaceModels()
      .then(() => setModelReady(true))
      .catch(() => toast.error("Face model failed to load. Check internet."))
      .finally(() => setModelLoading(false));
    reload();

    // Subscribe to online/offline transitions
    const unsub = subscribeOnline(({ online: on, size }) => {
      setOnline(on);
      setQueued(size);
      if (on && size > 0) syncQueue();
    });
    // Poll queue size (in case punch is made in another tab)
    const iv = setInterval(() => setQueued(queueSize()), 3000);
    // On mount: if online + has queue, drain immediately
    if (isOnline() && queueSize() > 0) syncQueue();
    return () => { unsub(); clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncQueue = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { ok, fail } = await drainAttendance((payload) =>
        apiClient().post("/attendance/punch", payload)
      );
      if (ok > 0) {
        toast.success(`Synced ${ok} offline punch${ok === 1 ? "" : "es"}${fail ? ` · ${fail} still pending` : ""}`);
        await reload();
      }
      setQueued(queueSize());
    } catch (e) {
      // Silent — will retry on next online event
    } finally {
      setSyncing(false);
    }
  };

  const reload = async () => {
    if (!isOnline()) return; // Skip network calls when offline
    try {
      const [w, s, h] = await Promise.all([
        apiClient().get("/workers"),
        apiClient().get("/attendance/summary"),
        apiClient().get("/attendance"),
      ]);
      setWorkers(w.data); setSummary(s.data); setHistory(h.data);
    } catch (_) { /* network hiccup — ignore */ }
  };

  return (
    <div className="space-y-6 fade-in-up" data-testid="attendance-page">
      <div>
        <h1 className="font-display text-5xl mt-1">Site Attendance</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Fraud-proof worker attendance. Face verification runs on the device, GPS is captured on every punch — <strong className="text-foreground">and it works offline</strong>. Punches sync automatically when the network returns.</p>
      </div>

      {/* Offline / Sync banner */}
      {(!online || queued > 0) && (
        <div
          data-testid="attendance-offline-banner"
          className={`border rounded-md p-3 flex items-center justify-between gap-3 text-sm ${!online ? "border-amber-500/60 bg-amber-500/10" : "border-primary/50 bg-primary/5"}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {!online ? (
              <><WifiOff className="h-4 w-4 text-amber-500 shrink-0" /> <span className="mono text-xs uppercase tracking-wider text-amber-600">Offline mode</span></>
            ) : (
              <><CloudUpload className="h-4 w-4 text-primary shrink-0" /> <span className="mono text-xs uppercase tracking-wider text-primary">Sync ready</span></>
            )}
            <span className="text-xs text-muted-foreground truncate">
              {queued > 0 ? `${queued} punch${queued === 1 ? "" : "es"} queued locally` : "Punches will queue on-device"}
            </span>
          </div>
          {online && queued > 0 && (
            <button
              onClick={syncQueue}
              disabled={syncing}
              data-testid="attendance-sync-btn"
              className="btn-sharp inline-flex items-center gap-1.5 border border-primary/60 text-primary text-xs px-3 py-1.5 hover:bg-primary/10 disabled:opacity-50"
            >
              {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <CloudUpload className="h-3 w-3" />}
              {syncing ? "Syncing…" : "Sync now"}
            </button>
          )}
        </div>
      )}

      {modelLoading && (
        <div className="border border-border bg-card p-4 flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading face recognition model (~6 MB)…
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-border bg-card p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Workers</div>
            <div className="font-display text-3xl mt-1">{summary.total_workers}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Punches Today</div>
            <div className="font-display text-3xl mt-1">{summary.punches_today}</div>
          </div>
          <div className="border border-border bg-card p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Matched</div>
            <div className="font-display text-3xl mt-1 text-emerald-500">{summary.matched_today}</div>
          </div>
        </div>
      )}

      <div className="inline-flex border border-border overflow-hidden">
        {[["punch", "Kiosk"], ["register", "Register Worker"], ["history", "History"]].map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)}
            data-testid={`att-tab-${k}`}
            className={`px-4 py-2 text-sm mono uppercase tracking-widest ${mode === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
            {l}
          </button>
        ))}
      </div>

      {mode === "register" && <RegisterPanel modelReady={modelReady} onDone={reload} />}
      {mode === "punch" && <PunchPanel modelReady={modelReady} workers={workers} onDone={reload} />}
      {mode === "history" && <HistoryPanel history={history} workers={workers} onDelete={reload} />}
    </div>
  );
}

const RegisterPanel = ({ modelReady, onDone }) => {
  const webcamRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", role: "worker" });
  const [busy, setBusy] = useState(false);

  const capture = async () => {
    if (!modelReady) { toast.error("Face model still loading. Wait a moment."); return; }
    if (!form.name || !form.phone) { toast.error("Enter name and phone first"); return; }
    setBusy(true);
    try {
      const shot = webcamRef.current?.getScreenshot();
      if (!shot) throw new Error("Camera not ready");
      const img = new Image();
      img.src = shot;
      await new Promise((res) => (img.onload = res));
      const descriptor = await computeDescriptor(img);
      if (!descriptor) { toast.error("No face detected. Adjust lighting/angle."); setBusy(false); return; }
      await apiClient().post("/workers", { ...form, face_descriptor: descriptor, face_photo_b64: shot });
      toast.success(`Registered ${form.name}`);
      setForm({ name: "", phone: "", role: "worker" });
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="border border-border bg-card p-5 space-y-3">
        <div className="font-heading text-lg">Worker details</div>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="worker-name" aria-label="Full name" className="w-full border border-border bg-background px-3 py-2 text-sm" />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid="worker-phone" aria-label="Phone" className="w-full border border-border bg-background px-3 py-2 text-sm" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
          data-testid="worker-role" className="w-full border border-border bg-background px-3 py-2 text-sm">
          {["worker", "mason", "electrician", "plumber", "carpenter", "helper", "foreman"].map(o => <option key={o}>{o}</option>)}
        </select>
        <button onClick={capture} disabled={busy} data-testid="worker-register-btn"
          className="btn-sharp w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {busy ? "Registering…" : "Capture Face & Register"}
        </button>
      </div>
      <div className="border border-border bg-card p-5">
        <div className="font-heading text-lg mb-2">Face capture</div>
        <div className="border border-border overflow-hidden bg-muted">
          <Webcam ref={webcamRef} audio={false} screenshotFormat="image/jpeg"
            videoConstraints={{ facingMode: "user", width: 480, height: 480 }}
            className="w-full aspect-square object-cover" />
        </div>
      </div>
    </div>
  );
};

const PunchPanel = ({ modelReady, workers, onDone }) => {
  const webcamRef = useRef(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [kind, setKind] = useState("check_in");
  const [busy, setBusy] = useState(false);
  const [gps, setGps] = useState(null);
  const [address, setAddress] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy }),
      () => setGps(null),
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Reverse geocode via OpenStreetMap Nominatim (free, no key). Debounced by GPS updates.
  useEffect(() => {
    if (!gps) { setAddress(null); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${gps.lat}&lon=${gps.lng}&zoom=16&addressdetails=1`;
        const r = await fetch(url, { headers: { "Accept-Language": "en" }, signal: ctrl.signal });
        const d = await r.json();
        const a = d.address || {};
        const parts = [
          a.road || a.pedestrian || a.neighbourhood || a.suburb,
          a.city || a.town || a.village || a.county,
          a.state,
          a.country,
        ].filter(Boolean);
        setAddress({
          short: parts.slice(0, 2).join(", "),
          full: d.display_name || parts.join(", "),
        });
      } catch { /* ignore */ }
    }, 900);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [gps?.lat, gps?.lng]);

  const punch = async () => {
    if (!modelReady) { toast.error("Face model still loading"); return; }
    if (!selectedWorker) { toast.error("Select a worker first"); return; }
    if (!gps) { toast.error("GPS not available. Enable location."); return; }
    setBusy(true); setLastResult(null);
    try {
      const shot = webcamRef.current?.getScreenshot();
      const img = new Image();
      img.src = shot;
      await new Promise((res) => (img.onload = res));
      const desc = await computeDescriptor(img);
      if (!desc) { toast.error("No face detected"); setBusy(false); return; }

      // Client-side quick check for UX
      const dist = distance(desc, selectedWorker.face_descriptor);
      const clientMatch = dist < 0.6;

      const payload = {
        worker_id: selectedWorker.id,
        face_descriptor: desc,
        latitude: gps.lat, longitude: gps.lng, accuracy: gps.acc,
        address: address?.short || address?.full || "",
        selfie_b64: shot, kind,
      };

      // OFFLINE MODE — queue the punch and confirm locally so worker can go on with their day.
      if (!isOnline()) {
        enqueueAttendance(payload);
        setLastResult({
          matched: clientMatch,
          worker_name: selectedWorker.name,
          match_distance: Number(dist.toFixed(3)),
          _offline: true,
        });
        toast.success(`Offline: ${kind === "check_in" ? "Checked in" : "Checked out"} ${selectedWorker.name} — will sync when online`);
        setBusy(false);
        return;
      }

      const { data } = await apiClient().post("/attendance/punch", payload);
      setLastResult(data);
      if (data.matched) toast.success(`${kind === "check_in" ? "Checked in" : "Checked out"}: ${data.worker_name}`);
      else toast.error(`Face mismatch (distance ${data.match_distance}). Punch recorded but flagged.`);
      onDone();
    } catch (err) {
      // Network error — treat as offline and queue.
      if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
        try {
          const shot = webcamRef.current?.getScreenshot();
          const img = new Image(); img.src = shot;
          await new Promise((res) => (img.onload = res));
          const desc = await computeDescriptor(img);
          if (desc) {
            enqueueAttendance({
              worker_id: selectedWorker.id, face_descriptor: desc,
              latitude: gps.lat, longitude: gps.lng, accuracy: gps.acc,
              address: address?.short || address?.full || "",
              selfie_b64: shot, kind,
            });
            toast.success(`Network down — punch queued. Auto-sync when back online.`);
          }
        } catch (_) {}
      } else {
        toast.error(err.response?.data?.detail || err.message || "Punch failed");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="border border-border bg-card p-5 space-y-3">
        <div className="font-heading text-lg">Kiosk</div>
        <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Worker</label>
        <select
          value={selectedWorker?.id || ""}
          onChange={(e) => setSelectedWorker(workers.find(w => w.id === e.target.value) || null)}
          data-testid="punch-worker"
          className="w-full border border-border bg-background px-3 py-2 text-sm">
          <option value="">— Select worker —</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name} · {w.role}</option>)}
        </select>

        <div className="inline-flex border border-border overflow-hidden">
          {["check_in", "check_out"].map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`punch-kind-${k}`}
              className={`px-4 py-2 text-sm mono uppercase tracking-widest ${kind === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              {k.replace("_", " ")}
            </button>
          ))}
        </div>

        <div className="text-sm">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Location</div>
          {gps ? (
            <>
              <div className="inline-flex items-center gap-1 text-emerald-500">
                <MapPin className="h-3 w-3" />
                <span className="mono text-xs">{gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} · ±{Math.round(gps.acc)}m</span>
              </div>
              <div className="mt-1 text-sm text-foreground" data-testid="punch-address">
                {address?.short || <span className="text-muted-foreground italic">Resolving address…</span>}
              </div>
              {address?.full && address.full !== address.short && (
                <div className="text-[11px] text-muted-foreground line-clamp-1">{address.full}</div>
              )}
            </>
          ) : (
            <span className="text-destructive">GPS not available</span>
          )}
        </div>

        <button onClick={punch} disabled={busy || !selectedWorker} data-testid="punch-btn"
          className="btn-sharp w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanFace className="h-4 w-4" />}
          {busy ? "Verifying…" : `Punch ${kind === "check_in" ? "In" : "Out"}`}
        </button>

        {lastResult && (
          <div className={`border p-3 mt-2 ${lastResult.matched ? "border-emerald-500/50 bg-emerald-500/5" : "border-destructive/50 bg-destructive/5"}`}
            data-testid="punch-result">
            <div className="mono text-[10px] uppercase tracking-widest">
              {lastResult.matched ? <span className="text-emerald-500 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> matched</span>
                                   : <span className="text-destructive inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> flagged</span>}
            </div>
            <div className="text-sm mt-1">{lastResult.worker_name} · distance {lastResult.match_distance}</div>
          </div>
        )}
      </div>
      <div className="border border-border bg-card p-5">
        <div className="font-heading text-lg mb-2">Live camera</div>
        <div className="border border-border overflow-hidden bg-muted">
          <Webcam ref={webcamRef} audio={false} screenshotFormat="image/jpeg"
            videoConstraints={{ facingMode: "user", width: 480, height: 480 }}
            className="w-full aspect-square object-cover" />
        </div>
      </div>
    </div>
  );
};

const HistoryPanel = ({ history, workers, onDelete }) => (
  <div className="space-y-4">
    <div className="border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border">
          <tr className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <th className="text-left px-4 py-3">Time</th>
            <th className="text-left px-4 py-3">Worker</th>
            <th className="text-left px-4 py-3">Kind</th>
            <th className="text-left px-4 py-3">Match</th>
            <th className="text-left px-4 py-3">Location</th>
          </tr>
        </thead>
        <tbody>
          {history.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No punches yet</td></tr>}
          {history.map((h) => (
            <tr key={h.id} className="border-b border-border" data-testid={`hist-${h.id}`}>
              <td className="px-4 py-2 mono text-xs">{new Date(h.created_at).toLocaleString()}</td>
              <td className="px-4 py-2">{h.worker_name}</td>
              <td className="px-4 py-2 mono text-xs uppercase">{h.kind.replace("_", " ")}</td>
              <td className="px-4 py-2">
                {h.matched ? <span className="text-emerald-500 mono text-xs">MATCH ({h.match_distance})</span>
                           : <span className="text-destructive mono text-xs">FLAG ({h.match_distance})</span>}
              </td>
              <td className="px-4 py-2 text-xs">
                {h.address || <span className="mono text-muted-foreground">{h.latitude?.toFixed(4)}, {h.longitude?.toFixed(4)}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="border border-border bg-card p-4">
      <div className="font-heading text-lg mb-2">Registered workers ({workers.length})</div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {workers.map((w) => (
          <div key={w.id} className="border border-border p-3 flex items-center gap-3" data-testid={`worker-card-${w.id}`}>
            {w.face_photo_b64 ? (
              <img src={w.face_photo_b64} className="w-12 h-12 object-cover border border-border" alt={w.name} />
            ) : <div className="w-12 h-12 bg-muted" />}
            <div className="flex-1 min-w-0">
              <div className="font-heading truncate">{w.name}</div>
              <div className="mono text-xs text-muted-foreground truncate">{w.phone} · {w.role}</div>
            </div>
            <button onClick={async () => { await apiClient().delete(`/workers/${w.id}`); onDelete(); }}
              data-testid={`worker-del-${w.id}`}
              className="text-muted-foreground hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  </div>
);
